import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { ChevronRight, ExternalLink, FileText, Link2, MoreHorizontal, Pencil, Plus, Star, Trash2 } from 'lucide-react'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'

import { DropLine, ROW_ACTIVE, ROW_IDLE, sidebarDragPreview } from './SidebarRows'
import { DOC_INDENT_PX } from './sidebarDnd'
import type { VisibleDocRow } from './sidebarDnd'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { useIsStarred, usePageMutations, useStarredMutations } from '@/lib/db/rxdb/hooks'
import { Icon, normalizeIconName } from '@/components/app/pickers/icon-picker'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'
import { createDragExtractor } from '@/lib/utils/dragDrop'
import { canCreateSubPage } from '@/routes/features/pages/utils/pageTreeUtils'

/** Docs nest up to this many levels in the sidebar; deeper ones open from the doc. */
export const SIDEBAR_DOC_LEVELS = 3
/** Marks a doc row's drop target so the tree can tell rows from its own empty space. */
export const SIDEBAR_DOC_ROW = 'sidebarDocRow'

const extractPageDragData = createDragExtractor('page')

export interface DocTreeItemProps {
  doc: PageDocument
  row: VisibleDocRow
  workspaceSlug: string
  spaceKey: string
  projectKey?: string
  currentPath: string
  pagesBasePath: string
  baseIndentPx: number
  isDragging: boolean
  /** The drop line drawn against this row, at the level the doc would land. */
  dropLine: { edge: 'top' | 'bottom'; depth: number } | null
  /** Outlined: the doc a drop would land inside. */
  isDropParent: boolean
  onToggle: (id: string) => void
  onCreateSubPage: (parentId: string) => void
  onDragStart: (id: string, grabOffsetX: number) => void
  onDragEnd: () => void
}

/**
 * One doc row in the space sidebar (design 22c). Every row keeps a 14px
 * chevron slot so docs with and without sub-pages line up; the whole row is
 * the drag source; "+" and "⋯" replace the right side on hover, and the ⋯
 * menu also opens on right-click.
 */
export function DocTreeItem({
  doc,
  row,
  workspaceSlug,
  spaceKey,
  projectKey,
  currentPath,
  pagesBasePath,
  baseIndentPx,
  isDragging,
  dropLine,
  isDropParent,
  onToggle,
  onCreateSubPage,
  onDragStart,
  onDragEnd,
}: DocTreeItemProps) {
  const { t } = useTranslation('projects')
  const { t: tNav } = useTranslation('navigation')
  const { t: tCommon } = useTranslation('common')
  const navigate = useNavigate()
  const { updatePage, deletePage } = usePageMutations()
  const itemRef = useRef<HTMLDivElement>(null)
  const renameRef = useRef<HTMLInputElement>(null)
  // The menu returns focus to its trigger on close, which would blur (and so
  // commit) the rename input the instant it mounts. Skip that when renaming.
  const renameRequestedRef = useRef(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)

  const activeSlug = currentPath.startsWith(`${pagesBasePath}/`)
    ? currentPath.slice(pagesBasePath.length + 1).split('/')[0] ?? null
    : null
  const isActive = activeSlug === doc.slug
  const iconName = normalizeIconName(doc.icon)
  const title = doc.title || t('sidebar.doc.untitled')
  const { depth } = row
  const contentLeft = depth * DOC_INDENT_PX + baseIndentPx

  useEffect(() => {
    const element = itemRef.current
    if (!element) return
    return combine(
      draggable({
        element,
        canDrag: () => !isRenaming,
        getInitialData: () => ({ type: 'page', id: doc.id }),
        onGenerateDragPreview: ({ nativeSetDragImage }) => sidebarDragPreview(nativeSetDragImage, element, title),
        onDragStart: ({ location }) => {
          const rect = element.getBoundingClientRect()
          onDragStart(doc.id, location.initial.input.clientX - (rect.left + contentLeft))
        },
        onDrop: () => onDragEnd(),
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => extractPageDragData(source.data) !== null,
        getData: () => ({ [SIDEBAR_DOC_ROW]: true, id: doc.id }),
      }),
    )
  }, [doc.id, title, contentLeft, isRenaming, onDragStart, onDragEnd])

  const open = () => {
    if (projectKey) {
      void navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
        params: { workspaceSlug, spaceKey, projectKey, pageSlug: doc.slug },
      })
    } else {
      void navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug',
        params: { workspaceSlug, spaceKey, pageSlug: doc.slug },
      })
    }
  }

  const startRename = () => {
    setRenameValue(doc.title)
    setIsRenaming(true)
    renameRequestedRef.current = true
  }

  const commitRename = async () => {
    const trimmed = renameValue.trim()
    setIsRenaming(false)
    if (!trimmed || trimmed === doc.title) return
    try {
      await updatePage(doc.id, { title: trimmed })
    } catch {
      toast.error(tNav('spaceNav.doc.renameFailed'))
    }
  }

  const canAddSub = canCreateSubPage(doc, depth)

  return (
    <>
      <div
        ref={itemRef}
        data-testid="sidebar-doc-row"
        data-doc-id={doc.id}
        data-drop-parent={isDropParent || undefined}
        className={cn(
          'group/row relative flex h-8 min-w-0 cursor-grab items-center gap-1.5 rounded-md pr-1.5 text-sm transition-colors active:cursor-grabbing',
          isActive ? ROW_ACTIVE : ROW_IDLE,
          // The lifted doc fades in place; its hover actions stay hidden while it's in the air.
          isDragging && 'opacity-40 hover:bg-transparent',
          isDropParent && 'bg-brand-tint text-foreground ring-1 ring-brand hover:bg-brand-tint',
        )}
        style={{ paddingLeft: `${contentLeft}px` }}
        onClick={open}
        onContextMenu={(e) => {
          e.preventDefault()
          setMenuOpen(true)
        }}
      >
        {/* Guide lines for each level this row sits under. */}
        {Array.from({ length: depth }, (_, level) => (
          <span
            key={level}
            aria-hidden
            className="pointer-events-none absolute inset-y-0 w-px bg-sidebar-border"
            style={{ left: level * DOC_INDENT_PX + baseIndentPx + 7 }}
          />
        ))}

        {dropLine && <DropLine edge={dropLine.edge} indentPx={dropLine.depth * DOC_INDENT_PX + baseIndentPx - 2} />}

        {/* The 14px chevron slot is always there, so every doc lines up. */}
        <button
          type="button"
          aria-label={row.isExpanded ? t('sidebar.collapse') : t('sidebar.expand')}
          aria-expanded={row.canExpand ? row.isExpanded : undefined}
          tabIndex={row.canExpand ? 0 : -1}
          disabled={!row.canExpand}
          className={cn(
            'flex size-3.5 shrink-0 items-center justify-center rounded text-muted-foreground transition-transform motion-reduce:transition-none',
            !row.canExpand && 'invisible',
            row.isExpanded && 'rotate-90',
          )}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(doc.id)
          }}
        >
          <ChevronRight className="size-3" />
        </button>

        <span data-drag-icon className="flex shrink-0 text-muted-foreground">
          {iconName ? <Icon name={iconName} className="size-3.5" /> : <FileText className="size-3.5" />}
        </span>

        {isRenaming ? (
          <input
            ref={renameRef}
            value={renameValue}
            aria-label={tNav('spaceNav.doc.renameAria')}
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setRenameValue(e.target.value)}
            onBlur={() => void commitRename()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void commitRename()
              }
              if (e.key === 'Escape') setIsRenaming(false)
            }}
            className="min-w-0 flex-1 border-b border-brand bg-transparent text-base outline-none md:text-sm"
          />
        ) : (
          <button
            type="button"
            className="min-w-0 flex-1 cursor-[inherit] truncate text-left"
            onClick={(e) => {
              e.stopPropagation()
              open()
            }}
          >
            {title}
          </button>
        )}

        {row.hiddenBelow > 0 && (
          <span
            className={cn(
              'shrink-0 text-xs tabular-nums text-muted-foreground group-hover/row:hidden',
              menuOpen && 'hidden',
            )}
          >
            +{row.hiddenBelow}
          </span>
        )}
        {/* A closed doc with sub-pages says how many; they open in place (design 32c). */}
        {row.canExpand && !row.isExpanded && (
          <span
            className={cn(
              'shrink-0 text-xs tabular-nums text-muted-foreground group-hover/row:hidden',
              menuOpen && 'hidden',
            )}
          >
            {row.childCount}
          </span>
        )}

        {/* "+" and "⋯" replace the right side on hover (design 22c / 23). */}
        <div
          className={cn(
            'hidden shrink-0 items-center gap-0.5 group-hover/row:flex group-focus-within/row:flex [@media(hover:none)]:flex',
            menuOpen && 'flex',
            isDragging && 'invisible',
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {canAddSub && (
            <button
              type="button"
              className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              aria-label={t('sidebar.doc.addSubAria')}
              title={t('sidebar.doc.addSubAria')}
              onClick={() => onCreateSubPage(doc.id)}
            >
              <Plus className="size-3.5" />
            </button>
          )}
          <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('docs.options')}
                className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="right"
              className="w-52"
              onCloseAutoFocus={(e) => {
                if (!renameRequestedRef.current) return
                renameRequestedRef.current = false
                e.preventDefault()
                renameRef.current?.focus()
                renameRef.current?.select()
              }}
            >
              <DropdownMenuItem onSelect={open}>
                <ExternalLink className="mr-2 size-4" />
                {tNav('spaceNav.doc.open')}
              </DropdownMenuItem>
              {canAddSub && (
                <DropdownMenuItem onSelect={() => onCreateSubPage(doc.id)}>
                  <Plus className="mr-2 size-4" />
                  {t('docs.addSubpage')}
                </DropdownMenuItem>
              )}
              <DocStarMenuItem docId={doc.id} />
              <DropdownMenuItem
                onSelect={() => {
                  void navigator.clipboard.writeText(`${window.location.origin}${pagesBasePath}/${doc.slug}`)
                  toast.success(t('sidebar.toast.linkCopied'))
                }}
              >
                <Link2 className="mr-2 size-4" />
                {t('sidebar.project.copyLink')}
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={startRename}>
                <Pencil className="mr-2 size-4" />
                {tNav('spaceNav.doc.rename')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive focus:text-destructive" onSelect={() => setConfirmDelete(true)}>
                <Trash2 className="mr-2 size-4" />
                {t('docs.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{tNav('spaceNav.doc.deleteTitle', { title })}</AlertDialogTitle>
            <AlertDialogDescription>{tNav('spaceNav.doc.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-white hover:bg-destructive/90"
              onClick={() => {
                void (async () => {
                  try {
                    await deletePage(doc.id)
                    toast.success(t('docs.deleted'))
                  } catch {
                    toast.error(t('docs.deleteFailed'))
                  }
                })()
              }}
            >
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

/** Favourite toggle; only mounted while the menu is open, so rows don't each query stars. */
function DocStarMenuItem({ docId }: { docId: string }) {
  const { t } = useTranslation('projects')
  const { isStarred, starredItem } = useIsStarred('page', docId)
  const { toggleStar } = useStarredMutations()
  return (
    <DropdownMenuItem onSelect={() => void toggleStar('page', docId, starredItem)}>
      <Star className={cn('mr-2 size-4', isStarred && 'fill-yellow-400 text-yellow-400')} />
      {isStarred ? t('sidebar.removeFavorite') : t('sidebar.addFavorite')}
    </DropdownMenuItem>
  )
}
